import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import * as Linking from 'expo-linking';
import usePayments from '../../../../../hooks/api/usePayments';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import { APITypes } from '../../../../../primary-record-types';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import BasePillSelect from '../../../../v2BaseComponents/Inputs/PillSelect';
import { getClientType } from '../../../../../hooks/useClientType';
import Text from '../../../../v2BaseComponents/Text';
import { useRegistrationCodeActionButton } from '../../../../shared/RegistrationCode';
import DetailsLayout from '../../../../v2BaseComponents/DetailsLayout';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';
import SubscriptionPlanDetails from '../../../../shared/SubscriptionPlanDetails';
import { Platform, View } from 'react-native';
import { useNestedNavigation } from '../../../../../utils/appNavigationNames';

const intl = getIntl('myAccount.subscription', {
    subscription: 'Subscription',
    monthly: 'Monthly',
    yearly: 'Yearly',
    family: 'Family Plan',
    monthlyCost: '/mo',
    iosYearlyCost: '/yr',
    benefits: 'Subscription Benefits',
    familyProfileCount: 'Manage up to 6 Medical Profiles',
    shareAccess: 'Share access with your Care Team',
    subscribeMonthly: 'Subscribe to Monthly Plan',
    subscribeYearly: 'Subscribe to Yearly Plan',
    subscribeLifetime: 'Subscribe to Lifetime Plan',
    monthlyDuration: 'Monthly',
    yearlyDuration: 'Yearly',
    lifetimeDuration: 'Lifetime',
    familyPlan: 'Family Plan',
    lifetime: 'Lifetime',
    lifetimeCost: '${cost} once',
    training: 'Received 2 hours of training & setup a $299 value',
    familyTitle: 'Family',
    header: 'Unlock unlimited access to premium features with a monthly or yearly plan',
    familyUnlimited: 'Family Unlimited',
    familyUnlimitedSubHeading:
        'Perfect for families navigating transitions, seeking second opinions, or managing a new diagnosis',
    unlimitedPortalRefreshes: 'Unlimited Patient Portal Refreshes',
    unlimitedAudioNotes: 'Unlimited Audio Notes with Transcripts',
    unlimitedChat: 'Unlimited Chat Assistant Questions',
    unlimitedData: 'Unlimited Data Uploads',
    invitationOrCouponCodeButtonText: 'Invitation or Coupon Code?',
    nativeWebhookDelayMessage: 'Wait a moment while we process your purchase',
});

export type AddSubscriptionOwnProps = {};

export type AddSubscriptionProps = AddSubscriptionOwnProps;

type Length = 'monthly' | 'yearly';

const AddSubscription: React.FC<AddSubscriptionProps> = () => {
    const [length, setLength] = React.useState<Length>('yearly');
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const registrationCodeActionButton = useRegistrationCodeActionButton();
    const { nestedAccountNavigation } = useNestedNavigation();
    const {
        createWebCheckoutSession,
        isLoadingConnectUrl,
        webProducts,
        isLoadingWebProducts,
        purchaseNativeProduct: purchaseMobileProduct,
        isPurchasingNativeProduct,
        isDelayingForNativeWebhookPropagation,
    } = usePayments({
        onMaybePurchaseNativeProductProduct: (status) => {
            if (status === 'purchased') {
                setPreRedirectArgs(
                    {
                        type: 'stripe',
                    },
                    () => nestedAccountNavigation('redirect', undefined),
                );
            }
        },
        onReceivedCheckoutSessionUrl: (url) => {
            setPreRedirectArgs(
                {
                    type: 'stripe',
                },
                () => {
                    Linking.openURL(url);
                },
            );
        },
    });

    const recordEvent = useRecordEvent();
    const getEventString = React.useCallback(() => {
        const prefix = `createCheckoutSession`;
        if (length === 'monthly') return `${prefix}FamilyPlanMonthly`;
        return `${prefix}FamilyPlanYearly`;
    }, [length]);

    const getTargetProductIdentifier = (): APITypes.paymentService.APIProductTypeIdentifiers => {
        return length === 'monthly' ? 'family-plan-monthly' : 'family-plan-yearly';
    };

    const getTargetProduct = () => webProducts.find((_) => _.productType === getTargetProductIdentifier());

    const getPriceString = () => `$${getTargetProduct()!.price}`;

    const getSubPriceString = () => {
        if (length === 'monthly') {
            return intl('monthlyCost');
        } else if (Platform.OS === 'ios') {
            return intl('iosYearlyCost');
        } else {
            return '';
        }
    };

    return (
        <>
            <AnimateLoadedContent
                isLoading={
                    isLoadingConnectUrl ||
                    !getTargetProduct() ||
                    isLoadingWebProducts ||
                    isPurchasingNativeProduct ||
                    isDelayingForNativeWebhookPropagation
                }
                message={isDelayingForNativeWebhookPropagation ? intl('nativeWebhookDelayMessage') : undefined}
            >
                {() => (
                    <ActionButtonLayout
                        actions={[
                            {
                                title: intl('invitationOrCouponCodeButtonText'),
                                show: registrationCodeActionButton.shouldShow,
                                onPress: registrationCodeActionButton.onPress,
                                isDisabled: isLoadingConnectUrl,
                                variant: 'secondary',
                                icon: 'Gift',
                            },
                        ]}
                    >
                        <DetailsLayout>
                            <MaxWidthRestrictedContent maxWidth={600}>
                                <MainContentContainer>
                                    <ContentStack>
                                        <Stack horizontal justify="center" fullWidth>
                                            <Text variant="h2" alignText="center">
                                                {intl('header')}
                                            </Text>
                                        </Stack>
                                        <Stack horizontal fullWidth justify="center">
                                            <BasePillSelect
                                                disableEmpty
                                                options={[
                                                    {
                                                        label: intl('yearly'),
                                                        value: 'yearly',
                                                    },
                                                    {
                                                        label: intl('monthly'),
                                                        value: 'monthly',
                                                    },
                                                ]}
                                                value={length}
                                                onChange={(value) => {
                                                    setLength(value as Length);
                                                }}
                                            />
                                        </Stack>
                                        <View style={{ width: '100%', alignItems: 'center', marginTop: 20 }}>
                                            <SubscriptionPlanDetails
                                                header={intl('familyUnlimited')}
                                                subHeader={intl('familyUnlimitedSubHeading')}
                                                price={getPriceString()}
                                                subPrice={getSubPriceString()}
                                                features={[
                                                    intl('unlimitedPortalRefreshes'),
                                                    intl('unlimitedAudioNotes'),
                                                    intl('unlimitedChat'),
                                                    intl('unlimitedData'),
                                                ]}
                                                actionButtonText={intl(
                                                    length === 'monthly' ? 'subscribeMonthly' : 'subscribeYearly',
                                                )}
                                                onActionButtonPress={() => {
                                                    recordEvent(getEventString());
                                                    if (Platform.OS === 'web') {
                                                        createWebCheckoutSession({
                                                            client: getClientType() as any,
                                                            productType: getTargetProduct()!.productType,
                                                        });
                                                    } else {
                                                        purchaseMobileProduct({
                                                            productType: getTargetProduct()!.productType,
                                                        });
                                                    }
                                                }}
                                                actionButtonTestID="subscribe-button"
                                            />
                                        </View>
                                    </ContentStack>
                                </MainContentContainer>
                            </MaxWidthRestrictedContent>
                        </DetailsLayout>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
            {registrationCodeActionButton.Prompt}
        </>
    );
};

export default React.memo(AddSubscription);
